/// <reference lib="dom" />
/**
 * File upload for a conversation: any kind, within its kind's limit, straight
 * to storage, resumable across tab closes (the checkpoint remembers the source
 * and its multipart upload, so the next attempt for the same file continues).
 *
 *   const uploader = createSourceUploader(sourceApi(conversationId, apiFetch));
 *   const { sourceId } = await uploader.upload(file, { onProgress });
 */

import { uploadMultipart, UploadError, type CheckpointStore, type UploadProgress } from './multipart.js';
import { indexedDbCheckpointStore } from './indexedDbStore.js';
import type { CompletedPart } from './resumable.js';

export interface SourceMultipart {
  uploadId: string;
  partSize: number;
  partCount: number;
  /** Presigned URLs for parts 1..partUrls.length; the rest come from partUrls(). */
  partUrls: string[];
  partUrlPath: string;
  expiresAt: string;
}

export interface SourceInitResponse {
  sourceId: string;
  multipart: SourceMultipart;
}

export interface SourceApi {
  init(file: { name: string; size: number; type: string }): Promise<SourceInitResponse>;
  complete(sourceId: string, parts: CompletedPart[]): Promise<{ sourceId: string; status: string }>;
  partUrls(sourceId: string, partNumbers: number[]): Promise<Record<number, string>>;
  /** Called when the person cancels: the server drops the upload and whatever it filed for it. */
  abort?(sourceId: string): Promise<unknown>;
}

export interface SourceUploaderOptions extends SourceApi {
  store?: CheckpointStore;
}

export interface UploadFileOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

type UploadableFile = Blob & { name: string; lastModified?: number };

interface ResumeRecord {
  sourceId: string;
  uploadId: string;
  partSize: number;
}

export function fileFingerprint(file: UploadableFile): string {
  return `source:${file.name}:${file.size}:${file.lastModified ?? 0}`;
}

const RESUME_SUFFIX = ':session';

async function readResume(store: CheckpointStore, key: string, size: number): Promise<ResumeRecord | null> {
  const cp = await store.get(key + RESUME_SUFFIX).catch(() => null);
  if (!cp || cp.totalBytes !== size) return null;
  return { sourceId: cp.ownerId, uploadId: cp.uploadId, partSize: cp.partSizeBytes };
}

function writeResume(store: CheckpointStore, key: string, size: number, r: ResumeRecord) {
  return store
    .put({ key: key + RESUME_SUFFIX, ownerId: r.sourceId, uploadId: r.uploadId, totalBytes: size, partSizeBytes: r.partSize, completed: {}, updatedAt: Date.now() })
    .catch(() => {});
}

export function createSourceUploader(options: SourceUploaderOptions) {
  const store = options.store ?? indexedDbCheckpointStore();

  async function run(file: UploadableFile, session: ResumeRecord, initialUrls: Record<number, string>, opts: UploadFileOptions) {
    const cached = { ...initialUrls };
    await uploadMultipart({
      key: fileFingerprint(file),
      ownerId: session.sourceId,
      body: file,
      contentType: file.type || 'application/octet-stream',
      uploadId: session.uploadId,
      partSizeBytes: session.partSize,
      partUrls: async (parts) => {
        const missing = parts.filter((n) => !cached[n]);
        if (missing.length) Object.assign(cached, await options.partUrls(session.sourceId, missing));
        return Object.fromEntries(parts.map((n) => [n, cached[n]]));
      },
      complete: async (parts) => {
        await options.complete(session.sourceId, parts);
      },
      store,
      onProgress: opts.onProgress,
      signal: opts.signal,
    });
  }

  async function fresh(file: UploadableFile, opts: UploadFileOptions) {
    const init = await options.init({ name: file.name, size: file.size, type: file.type || 'application/octet-stream' });
    const session = { sourceId: init.sourceId, uploadId: init.multipart.uploadId, partSize: init.multipart.partSize };
    await writeResume(store, fileFingerprint(file), file.size, session);
    const urls = Object.fromEntries(init.multipart.partUrls.map((u, i) => [i + 1, u]));
    await run(file, session, urls, opts);
    return session.sourceId;
  }

  async function cancel(key: string, size: number) {
    const open = await readResume(store, key, size);
    await store.delete(key).catch(() => {});
    await store.delete(key + RESUME_SUFFIX).catch(() => {});
    if (open) await options.abort?.(open.sourceId).catch(() => {});
  }

  return {
    async upload(file: UploadableFile, opts: UploadFileOptions = {}): Promise<{ sourceId: string; resumed: boolean }> {
      const key = fileFingerprint(file);
      const resume = await readResume(store, key, file.size);
      let sourceId: string;
      let resumed = false;
      try {
        if (resume) {
          try {
            await run(file, resume, {}, opts);
            sourceId = resume.sourceId;
            resumed = true;
          } catch (err) {
            if (!(err instanceof UploadError) || err.reason !== 'gone') throw err;
            await store.delete(key).catch(() => {});
            sourceId = await fresh(file, opts);
          }
        } else {
          sourceId = await fresh(file, opts);
        }
      } catch (err) {
        if (opts.signal?.aborted) await cancel(key, file.size);
        throw err;
      }
      await store.delete(key + RESUME_SUFFIX).catch(() => {});
      return { sourceId, resumed };
    },
  };
}

export type JsonFetch = (path: string, init?: { method?: string; body?: unknown }) => Promise<Response>;

async function serverMessage(resp: Response): Promise<string | null> {
  const body = (await resp.json().catch(() => null)) as { error?: unknown } | null;
  return typeof body?.error === 'string' && body.error ? body.error : null;
}

async function json<T>(resp: Response, what: string): Promise<T> {
  if (resp.status === 401) throw new UploadError('Auth failed — sign in again', 'auth');
  if (resp.status === 404 || resp.status === 409) throw new UploadError(`${what}: the upload is no longer open (${resp.status})`, 'gone');
  if (!resp.ok) throw new UploadError((await serverMessage(resp)) ?? `${what} failed (${resp.status})`, 'server');
  return (await resp.json()) as T;
}

/** The api-server routes (`/conversations/:id/sources/*`) over the caller's authenticated fetch. */
export function sourceApi(conversationId: string, fetchJson: JsonFetch, options: { use?: 'asset' } = {}): SourceApi {
  const base = `conversations/${encodeURIComponent(conversationId)}/sources`;
  return {
    init: async (file) => json(await fetchJson(`${base}/init`, { method: 'POST', body: { ...file, ...options } }), 'Upload start'),
    complete: async (sourceId, parts) =>
      json(await fetchJson(`${base}/${sourceId}/complete`, { method: 'POST', body: { parts } }), 'Upload completion'),
    partUrls: async (sourceId, partNumbers) => {
      const out = await json<{ urls: Record<string, string> }>(
        await fetchJson(`${base}/${sourceId}/parts?parts=${partNumbers.join(',')}`),
        'Part URLs',
      );
      return Object.fromEntries(Object.entries(out.urls).map(([k, v]) => [Number(k), v]));
    },
    abort: async (sourceId) => fetchJson(`${base}/${sourceId}/abort`, { method: 'POST' }),
  };
}
